import React, { useState, useEffect, useRef } from 'react';

interface SpeechRecognitionProps {
  targetWord: string;
  onResultCallback?: (score: number, transcript: string) => void;
}

// Khai báo SpeechRecognition cho TypeScript
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const SpeechRecognitionComponent: React.FC<SpeechRecognitionProps> = ({ targetWord, onResultCallback }) => {
  const [isListening, setIsListening] = useState<boolean>(false);
  const [transcript, setTranscript] = useState<string>('');
  const [score, setScore] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);

  // Khởi tạo SpeechRecognition khi component mount
  useEffect(() => {
    // Kiểm tra xem trình duyệt có hỗ trợ Speech Recognition không
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setError('Trình duyệt của bạn không hỗ trợ nhận dạng giọng nói.');
      return;
    }
    
    // Tạo instance của SpeechRecognition
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;
    recognitionRef.current.lang = 'en-US'; // Sử dụng tiếng Anh
    
    // Xử lý kết quả
    recognitionRef.current.onresult = (event: any) => {
      const current = event.resultIndex;
      const userTranscript = event.results[current][0].transcript.trim().toLowerCase();
      const targetLower = targetWord.toLowerCase();
      
      setTranscript(userTranscript);
      
      // Tính điểm dựa trên độ tương đồng
      const score = calculateSimilarity(userTranscript, targetLower);
      setScore(score);
      
      // Gọi callback nếu có
      if (onResultCallback) {
        onResultCallback(score, userTranscript);
      }
      
      setIsListening(false);
    };
    
    // Xử lý lỗi
    recognitionRef.current.onerror = (event: any) => {
      setIsListening(false);
      setError(`Lỗi nhận dạng giọng nói: ${event.error}`);
    };
    
    // Xử lý khi kết thúc
    recognitionRef.current.onend = () => {
      setIsListening(false);
    };
    
    // Cleanup khi component unmount
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [targetWord, onResultCallback]);
  
  // Bắt đầu nhận dạng giọng nói
  const startListening = () => {
    setError(null);
    setTranscript('');
    setScore(null);
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Error starting speech recognition:', err);
        setError('Không thể bắt đầu nhận dạng giọng nói. Vui lòng thử lại.');
      }
    }
  };
  
  // Dừng nhận dạng giọng nói
  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };
  
  // Tính điểm dựa trên độ tương đồng của 2 chuỗi
  // Sử dụng Levenshtein distance algorithm
  const calculateSimilarity = (str1: string, str2: string): number => {
    if (str1 === str2) return 100; // Hoàn toàn giống nhau
    
    // Tính khoảng cách Levenshtein
    const track = Array(str2.length + 1).fill(null).map(() => 
      Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i += 1) {
      track[0][i] = i;
    }
    
    for (let j = 0; j <= str2.length; j += 1) {
      track[j][0] = j;
    }
    
    for (let j = 1; j <= str2.length; j += 1) {
      for (let i = 1; i <= str1.length; i += 1) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        track[j][i] = Math.min(
          track[j][i - 1] + 1, // xóa
          track[j - 1][i] + 1, // thêm
          track[j - 1][i - 1] + indicator, // thay thế
        );
      }
    }
    
    // Tính toán độ tương đồng dựa trên khoảng cách
    const distance = track[str2.length][str1.length];
    const maxLength = Math.max(str1.length, str2.length);
    
    // Chuyển đổi thành phần trăm (100% = hoàn toàn giống nhau)
    return Math.round((1 - distance / maxLength) * 100);
  };
  
  // Lấy trạng thái màu dựa trên điểm số
  const getScoreColor = () => {
    if (score === null) return '';
    if (score >= 90) return 'text-emerald-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  // Lấy đánh giá dựa trên điểm số
  const getScoreRating = () => {
    if (score === null) return '';
    if (score >= 90) return 'Tuyệt vời!';
    if (score >= 70) return 'Khá tốt!';
    if (score >= 50) return 'Cần cải thiện';
    return 'Thử lại';
  };
  
  return (
    <div className="speech-recognition-container">
      {error && (
        <div className="speech-error-message">
          {error}
        </div>
      )}
      
      <div className="speech-controls">
        <button
          onClick={isListening ? stopListening : startListening}
          disabled={!recognitionRef.current}
          className={`speech-button ${isListening ? 'listening' : ''}`}
        >
          {isListening ? (
            <>
              <span className="recording-dot"></span>
              Đang nghe...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
              Đọc từ này
            </>
          )}
        </button>
      </div>
      
      {transcript && (
        <div className="speech-result">
          <div className="transcript">
            <span className="transcript-label">Bạn đã đọc:</span> 
            <span className="transcript-text">{transcript}</span>
          </div>
          
          <div className={`score ${getScoreColor()}`}>
            <span className="score-value">{score}%</span>
            <span className="score-rating">{getScoreRating()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeechRecognitionComponent;